import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes, FaBullseye, FaClock, FaGamepad } from "react-icons/fa";
import { useGameSearchInfinite } from "@/hooks/overwolf/useGameSearchInfinite";
import { OWGameResponse } from "@devour/client";
import styles from "./GameSearchInline.module.scss";

interface GameSearchInlineProps {
    onGameSelect?: (game: OWGameResponse) => void;
    selectedGame?: OWGameResponse | null;
    compact?: boolean; // Compact mode for game view
}

const GameSearchInline: React.FC<GameSearchInlineProps> = ({ onGameSelect, selectedGame, compact = false }) => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState<"all" | "quests" | "none">("none");
    const [showDropdown, setShowDropdown] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    // Use infinite scroll hook with server-side filtering
    const category = filterMode === "quests" ? "events" : filterMode === "all" ? "all" : undefined;
    const {
        games,
        totalGames,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGameSearchInfinite({
        search: searchTerm.trim() || undefined,
        category,
    });

    // Games are already filtered server-side
    const filteredGames = searchTerm.trim() || filterMode !== "none" ? games : [];

    // Sort games alphabetically - EXACT same as modal
    const sortedGames = useMemo(() => {
        if (!filteredGames || filteredGames.length === 0) {
            return [];
        }

        return [...filteredGames].sort((a, b) => {
            if (!a || !b) return 0;

            const aName = a.name || "";
            const bName = b.name || "";
            return aName.localeCompare(bName);
        });
    }, [filteredGames]);

    // Show all games - CSS handles scrolling with max-height
    const displayGames = sortedGames;

    const handleGameClick = useCallback((game: OWGameResponse) => {
        if (onGameSelect) {
            onGameSelect(game);
            setSearchTerm(game.name);
            setShowDropdown(false);
        } else if (game && game.slug) {
            // Navigate to game page like the modal does
            navigate(`/games/${game.slug}`);
        }
    }, [navigate, onGameSelect]);

    // Handle clicking outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Calculate dropdown position for compact mode
    useEffect(() => {
        if (compact && showDropdown && inputRef.current && containerRef.current) {
            const input = inputRef.current;
            const container = containerRef.current;
            const inputRect = input.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            container.style.setProperty("--dropdown-left", `${containerRect.left}px`);
            container.style.setProperty("--dropdown-top", `${inputRect.bottom + 4}px`);
            container.style.setProperty("--dropdown-width", `${containerRect.width}px`);
        }
    }, [compact, showDropdown]);

    // Infinite scroll observer for dropdown
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage && showDropdown) {
                    fetchNextPage();
                }
            },
            { threshold: 0.1 },
        );

        if (loadMoreRef.current && showDropdown) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, showDropdown]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            setShowDropdown(false);
            setFocusedIndex(-1);
        } else if (e.key === "ArrowDown" && showDropdown) {
            e.preventDefault();
            setFocusedIndex(prev =>
                prev < displayGames.length - 1 ? prev + 1 : prev);
        } else if (e.key === "ArrowUp" && showDropdown) {
            e.preventDefault();
            setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === "Enter" && focusedIndex >= 0) {
            e.preventDefault();
            handleGameClick(displayGames[focusedIndex]);
        }
    }, [showDropdown, focusedIndex, displayGames, handleGameClick]);

    return (
        <div className={`${styles.container} ${compact ? styles.compact : ""}`} ref={containerRef}>
            {!compact &&
                <div className={styles.header}>
                    <h4 className={styles.title}>
                        <FaGamepad className={styles.headerIcon} />
                        What are you playing today?
                    </h4>
                </div>
            }

            <div className={styles.searchWrapper}>
                <div className={styles.searchBox}>
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.searchInput}
                        placeholder="🔍 Search games..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            // Show dropdown if there's text or a filter is selected
                            setShowDropdown(e.target.value.trim().length > 0 || filterMode !== "none");
                            setFocusedIndex(-1);
                        }}
                        onFocus={() => {
                            // Only show dropdown if there's text or a filter is selected
                            setShowDropdown(searchTerm.trim().length > 0 || filterMode !== "none");
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    {searchTerm &&
                        <button
                            className={styles.clearBtn}
                            onClick={() => {
                                setSearchTerm("");
                                // Close dropdown if no filter is selected
                                if (filterMode === "none") {
                                    setShowDropdown(false);
                                }
                                inputRef.current?.focus();
                            }}
                            aria-label="Clear search"
                        >
                            <FaTimes />
                        </button>
                    }
                </div>

                <div className={styles.filters}>
                    <button
                        className={`${styles.filterBtn} ${filterMode === "quests" ? styles.filterBtnActive : ""}`}
                        onClick={() => {
                            // Toggle quest filter - if already selected, deselect
                            const newMode = filterMode === "quests" ? "none" : "quests";
                            setFilterMode(newMode);
                            // Show dropdown if selecting, hide if deselecting and no search term
                            setShowDropdown(newMode !== "none" || searchTerm.trim().length > 0);
                        }}
                        title="Games with quests and rewards"
                    >
                        <FaBullseye />
                    </button>
                    <button
                        className={`${styles.filterBtn} ${filterMode === "all" ? styles.filterBtnActive : ""}`}
                        onClick={() => {
                            // Toggle all filter - if already selected, deselect
                            const newMode = filterMode === "all" ? "none" : "all";
                            setFilterMode(newMode);
                            // Show dropdown if selecting, hide if deselecting and no search term
                            setShowDropdown(newMode !== "none" || searchTerm.trim().length > 0);
                        }}
                        title="All time-reward games"
                    >
                        <FaClock />
                    </button>
                </div>
            </div>

            {/* Dropdown Results */}
            {showDropdown && !isLoading && displayGames.length > 0 &&
                <div className={styles.dropdown}>
                    <div className={styles.dropdownHeader}>
                        {totalGames} games available
                    </div>
                    <div className={styles.gamesList}>
                        {displayGames.map((game, index) =>
                            <button
                                key={game.gameId || game.id}
                                className={`${styles.gameItem} ${
                                    index === focusedIndex ? styles.gameItemFocused : ""
                                }`}
                                onClick={() => handleGameClick(game)}
                                onMouseEnter={() => setFocusedIndex(index)}
                            >
                                <span className={styles.gameName}>{game.name}</span>
                                {(game as any).steamData?.genres?.[0] &&
                                    <span className={styles.gameGenre}>
                                        {(game as any).steamData.genres[0]}
                                    </span>
                                }
                                <span className={styles.viewBtn}>View</span>
                            </button>)}

                        {/* Infinite scroll trigger */}
                        {hasNextPage &&
                            <div ref={loadMoreRef} className={styles.loadMore}>
                                {isFetchingNextPage
                                    ? <div className={styles.loadingMore}>
                                        Loading more games...
                                    </div>
                                    : <div style={{ height: 1 }} />
                                }
                            </div>
                        }
                    </div>
                </div>
            }

            {/* Loading State */}
            {isLoading &&
                <div className={styles.loading}>
                    Loading games...
                </div>
            }

            {/* Selected Game Display - Hide in compact mode */}
            {!compact && selectedGame &&
                <div className={styles.selected}>
                    Selected: <strong>{selectedGame.name}</strong>
                </div>
            }
        </div>
    );
};

export default GameSearchInline;